<template>
	<div ref="wrapper" class="input-wrapper">
		<div class="txt-input-label" v-if="label"><label :for="id">{{ label }}</label></div>

		<div ref="element" class="row input-element">
			<div @click="setValue(currentValue - increment)" class="input-btn minus-btn" :class="{disabled: min !== null && currentValue <= min}">
				<svg xmlns="http://www.w3.org/2000/svg" width="14.985" height="14.985" viewBox="0 0 14.985 14.985">
					<path d="M133.812,142.288a1.063,1.063,0,1,1-1.5-1.5l8.477-8.477a1.063,1.063,0,1,1,1.5,1.5Z" transform="translate(7.493 -186.676) rotate(45)"/>
				</svg>
			</div>
			<div class="input-number-container" :data-symbol="symbol" :class="{'right-side-symbol':rightSideSymbol}">
				<input ref="input" type="number" inputmode="decimal" :name="name" :id="id" @focusin="selectValue" @focusout="finishEditing" @keyup.enter="pressEnter">
			</div>
			<div @click="setValue(currentValue + increment)" class="input-btn plus-btn" :class="{disabled: max !== null && currentValue >= max}">
				<svg xmlns="http://www.w3.org/2000/svg" width="14.985" height="14.985" viewBox="0 0 14.985 14.985">
					<path d="M142.288,142.288a1.073,1.073,0,0,1-1.5,0L137.3,138.8l-3.486,3.486a1.063,1.063,0,1,1-1.5-1.5l3.487-3.487-3.487-3.486a1.063,1.063,0,0,1,1.5-1.5l3.486,3.487,3.487-3.487a1.063,1.063,0,1,1,1.5,1.5L138.8,137.3l3.486,3.487A1.073,1.073,0,0,1,142.288,142.288Z" transform="translate(7.493 -186.676) rotate(45)"/>
				</svg>
			</div>
		</div>

	</div>
</template>

<script>
import {getRandomAlphanumericString} from "@/shared/helpers/strings";

export default {
	name: "AppNumberInput",
	emits: ["changed-value", "pressed-enter"],
	data() {
		return {
			currentValue: 0,
		}
	},
	props: {
		label: {type: String, default: null},
		name: {type: String, default: "input-number"},
		value: {type: Number, default: 0},
		max: {type: Number, default: null},
		min: {type: Number, default: null},
		increment: {type: Number, default: 1},
		doubleDecimal: {type: Boolean, default: false},
		symbol: {type: String, default: ''},
		rightSideSymbol: {type: Boolean, default: false},
	},
	methods: {
		formatInputPrice(price) {
			let finalPrice = price && !isNaN(price)? Number(price) : 0;
			let isInt = !isNaN(finalPrice) && parseInt(Number(finalPrice)) == finalPrice && !isNaN(parseInt(finalPrice, 10));
			return Number(finalPrice).toFixed(isInt ? 0 : 2)
		},
		changeValue(val) {
			let validVal = getValidNumber(val, this.min, this.max);
			if(this.$refs && this.$refs.input) {
				if(this.doubleDecimal) {
					this.$refs.input.value = this.formatInputPrice(validVal);
				} else {
					this.$refs.input.value = validVal+"";
				}
			}
			const changed = this.currentValue !== validVal;
			this.currentValue = validVal;
			if(changed || validVal !== val) {
				this.$emit("changed-value", validVal);
			}
			return validVal;
		},
		setValue(val) {
			this.changeValue(val);
		},
		pressEnter(e) {
			this.setValue(e.target.value);
			this.$emit('pressed-enter', this.currentValue);
		},
		finishEditing(e) {
			this.setValue(e.target.value);
		},
		selectValue(e) {
			e.target.select();
		}
	},
	computed: {
		id() {
			return "input-text-"+getRandomAlphanumericString();
		},
	},
	mounted() {
		this.currentValue = this.value;
		this.changeValue(this.value);
	},
	watch: {
		value(val) {
			this.changeValue(val);
		},
		max() {
			this.setValue(this.currentValue);
		},
		min() {
			this.setValue(this.currentValue);
		},
	}
}

function getValidNumber(val, min, max) {
	let v = val && !isNaN(val) ? Number(val) : 0;
	if (max !== null && v > max) {
		v = max;
	}
	if (min !== null && v < min) {
		v = min;
	}
	return v;
}
</script>

<style lang="scss" scoped>

.input-wrapper {
	width: 120px;

	.input-element {
		justify-content: space-between;
		height: 35px;
		box-shadow: 0 1px 2px var(--color-box-shadow-3);
		border-radius: 13px;
		padding: 4px 0;
		background-color: var(--color-bg);

		.input-number-container {
			width: 70px;
			box-shadow: inset 0 1px 2px var(--color-box-shadow-3);
			background-color: var(--color-bg-primary-2);
			border-radius: 13px;
			position: relative;

			&:before{
				@include noSelect();
				pointer-events: none;
				content: attr(data-symbol);
				position: absolute;
				left: 12px;
				top: 50%;
				transform: translate(-50%, -50%);
				font-size: 14px;
				color: rgba(var(--rgb-txt),.6);
				display: none;
			}
			&.right-side-symbol {
				&:before{
					left: auto;
					right: 12px;
					transform: translate(50%, -50%);
				}
			}

			input {
				width: 100%;
				height: 100%;
				text-align: center;
				border: none;
				background-color: transparent;
				color: var(--color-txt);
				padding: 0 4px;
				@include textSelect();

				&::-webkit-outer-spin-button,
				&::-webkit-inner-spin-button {
					-webkit-appearance: none;
					margin: 0;
				}

				&[type=number] {
					-moz-appearance: textfield;
				}
			}

			&[data-symbol]:not([data-symbol=""]) {
				padding-left: 20px;
				padding-right: 5px;
				&:before{ display: block; }
				input {
					text-align: left;
					padding: 0;
				}
				&.right-side-symbol {
					padding-left: 5px;
					padding-right: 20px;
					input {
						text-align: right;
					}
				}
			}
		}

		.input-btn {
			@include centered;
			width: 22px;
			padding: 0 4px;
			height: 100%;
			cursor: pointer;

			& * {
				@include noSelect;
			}

			&:nth-of-type(1) {
				padding-left: 6px;
			}

			&:nth-of-type(3) {
				padding-right: 6px;
			}

			svg {
				width: 100%;

				path {
					fill: $primary;
				}
			}
		}
	}

	@media (max-width: 768px) {
		width: 100px;

		.input-element {
			height: 30px;

			.input-number-container {
				width: 55px;

				&:before{
					left: 8px;
					font-size: 12px;
				}
				&.right-side-symbol:before{
					left: auto;
					right: 8px;
				}

				&[data-symbol]:not([data-symbol=""]) {
					padding-left: 14px;
					padding-right: 3px;
					&.right-side-symbol {
						padding-right: 14px;
						padding-left: 3px;
					}

					input{
						font-size: 14px;
					}
				}
			}
		}
	}

}

</style>